import { z } from "zod";
export const ailanStorePlatformSchema = z.enum(["kuaishou", "douyin", "pdd", "tmall"]);
export const hermesStoreSchema = z.object({
    id: z.string().trim().min(1).max(160),
    platform: ailanStorePlatformSchema,
    name: z.string().trim().min(1).max(160),
    owner: z.string().trim().min(1).max(80),
    businessUnit: z.string().trim().min(1).max(80),
    metadata: z.object({ reviewRequired: z.boolean().optional() }).passthrough().optional(),
}).strict();
export const hermesAgentPerformanceSchema = z.object({
    sourceRecordId: z.string().trim().min(1).max(160), storeId: z.string().trim().min(1).max(160),
    periodStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), periodEnd: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    collectedAt: z.string().datetime(), agentName: z.string().trim().min(1).max(120), subAccountName: z.string().max(120).optional(),
    receptionCount: z.number().int().nonnegative().default(0), inquiryCount: z.number().int().nonnegative().default(0),
    orderCount: z.number().int().nonnegative().default(0), netSalesAmount: z.number().default(0), conversionRate: z.number().min(0).max(100).default(0),
    conversionBaseCount: z.number().int().nonnegative().default(0), conversionDelayed: z.boolean().default(false), avgReplySeconds: z.number().nonnegative().default(0),
    satisfactionRate: z.number().min(0).max(100).default(0), dissatisfactionRate: z.number().min(0).max(100).default(0),
    reviewCount: z.number().int().nonnegative().default(0), fiveStarCount: z.number().int().nonnegative().default(0), fourStarCount: z.number().int().nonnegative().default(0),
    provenance: z.object({ source: z.string().max(80), capturedAt: z.string().datetime().optional(), checksum: z.string().max(128).optional() }).strict(),
}).strict();
export const hermesShopMetricSchema = z.object({
    sourceRecordId: z.string().trim().min(1).max(160), storeId: z.string().trim().min(1).max(160), metricDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    collectedAt: z.string().datetime(), metricType: z.string().trim().min(1).max(80), score: z.number().nullable().optional(), valueText: z.string().max(255).nullable().optional(),
    metrics: z.record(z.union([z.string(), z.number(), z.boolean(), z.null()])),
    provenance: z.object({ source: z.string().max(80), capturedAt: z.string().datetime().optional(), checksum: z.string().max(128).optional() }).strict(),
}).strict();
export const conversationSenderSchema = z.enum(["CUSTOMER", "AGENT", "SYSTEM"]);
export const hermesConversationMessageSchema = z.object({
    id: z.string().trim().min(1).max(160), sender: conversationSenderSchema,
    sentAt: z.string().datetime(), content: z.string().trim().min(1).max(10_000),
}).strict();
export const hermesConversationSchema = z.object({
    sourceConversationId: z.string().trim().min(1).max(160), storeId: z.string().trim().min(1).max(160),
    agentName: z.string().trim().min(1).max(120), customerName: z.string().trim().max(120).nullable().optional(),
    orderId: z.string().trim().max(160).nullable().optional(), yellowFlagged: z.boolean().nullable().optional(),
    startedAt: z.string().datetime(), endedAt: z.string().datetime().nullable().optional(), collectedAt: z.string().datetime(),
    messages: z.array(hermesConversationMessageSchema).min(1).max(500),
    provenance: z.object({ source: z.string().max(80), capturedAt: z.string().datetime().optional(), checksum: z.string().max(128).optional() }).strict(),
}).strict();
export const hermesCollectExportSchema = z.object({
    run: z.object({ id: z.string().trim().min(1).max(120), exportedAt: z.string().datetime(), startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), sourceVersion: z.string().trim().min(1).max(80) }).strict(),
    stores: z.array(hermesStoreSchema), shopMetrics: z.array(hermesShopMetricSchema), agentPerformance: z.array(hermesAgentPerformanceSchema),
    conversations: z.array(hermesConversationSchema).default([]),
}).strict();
export const authUserSchema = z.object({ id: z.string(), username: z.string(), displayName: z.string(), role: z.enum(["ADMIN", "VIEWER"]), csrfToken: z.string() });
export const syncStatusSchema = z.object({ configured: z.boolean(), ready: z.boolean(), intervalMinutes: z.number().int().positive(), allowedStoreCount: z.number().int(), lastRun: z.object({ id: z.string(), status: z.enum(["RUNNING", "SUCCEEDED", "FAILED"]), startedAt: z.string().datetime(), completedAt: z.string().datetime().nullable(), errorCode: z.string().nullable() }).nullable() });
export const qualityIssueTypeSchema = z.enum(["ATTITUDE", "NO_REPLY", "SLOW_REPLY", "IRRELEVANT_REPLY", "MISSING_YELLOW_FLAG"]);
export const qualitySeveritySchema = z.enum(["LOW", "MEDIUM", "HIGH"]);
export const qualityReviewStatusSchema = z.enum(["PENDING", "CORRECTING", "COMPLETED"]);
export const qualityIssueSchema = z.object({
    id: z.string(), type: qualityIssueTypeSchema, severity: qualitySeveritySchema,
    title: z.string(), reason: z.string(), evidence: z.string(), suggestion: z.string(), messageId: z.string().nullable(),
});
export const qualityMessageSchema = z.object({
    id: z.string(), sender: conversationSenderSchema, sentAt: z.string().datetime(), content: z.string(), flagged: z.boolean(),
});
export const qualitySessionSchema = z.object({
    id: z.string(), sourceConversationId: z.string(), platform: ailanStorePlatformSchema, storeName: z.string(),
    agentName: z.string(), customerName: z.string().nullable(), orderId: z.string().nullable(), yellowFlagged: z.boolean().nullable(),
    startedAt: z.string().datetime(), endedAt: z.string().datetime().nullable(),
    collectedAt: z.string().datetime(), score: z.number().int().min(0).max(100), status: qualityReviewStatusSchema,
    summary: z.string(), source: z.string(), messages: z.array(qualityMessageSchema), issues: z.array(qualityIssueSchema),
});
export const qualityAgentSummarySchema = z.object({
    agentName: z.string(), sessionCount: z.number().int().nonnegative(), issueSessionCount: z.number().int().nonnegative(),
    averageScore: z.number().min(0).max(100).nullable(), mainIssue: z.string().nullable(),
    captureStatus: z.enum(["CAPTURED", "WAITING"]),
});
export const qualityDashboardResponseSchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    summary: z.object({ sessionCount: z.number().int().nonnegative(), issueSessionCount: z.number().int().nonnegative(), noReplyCount: z.number().int().nonnegative(), attitudeCount: z.number().int().nonnegative(), missingYellowFlagCount: z.number().int().nonnegative(), agentCount: z.number().int().nonnegative() }),
    agents: z.array(qualityAgentSummarySchema), sessions: z.array(qualitySessionSchema),
});
export const platformSchema = z.enum([
    "TAOBAO_TMall",
    "JD",
    "DOUYIN",
    "PINDUODUO",
    "XIAOHONGSHU",
    "KUAISHOU",
]);
export const loginStatusSchema = z.enum(["ONLINE", "LATE", "OFFLINE"]);
export const issueTypeSchema = z.enum(["SATISFACTION", "REPLY_TIME", "LOGIN"]);
export const alertStatusSchema = z.enum(["OPEN", "ACKNOWLEDGED", "RESOLVED"]);
export const platformLabels = {
    TAOBAO_TMall: "淘宝/天猫",
    JD: "京东",
    DOUYIN: "抖音",
    PINDUODUO: "拼多多",
    XIAOHONGSHU: "小红书",
    KUAISHOU: "快手",
};
export const patrolPolicySchema = z.object({
    platform: platformSchema,
    minSatisfaction: z.number().min(0).max(100),
    maxReplySeconds: z.number().int().positive(),
    loginDeadline: z.string().regex(/^\d{2}:\d{2}$/),
});
export const patrolSettingsSchema = z.object({
    enabled: z.boolean(),
    intervalMinutes: z.number().int().min(1).max(1440),
    policies: z.array(patrolPolicySchema).length(6),
});
export const issueSchema = z.object({
    type: issueTypeSchema,
    label: z.string(),
    actual: z.number().nullable(),
    threshold: z.number().nullable(),
    status: alertStatusSchema,
});
export const storeHealthSchema = z.object({
    storeId: z.string(),
    platform: platformSchema,
    storeName: z.string(),
    agentName: z.string(),
    satisfaction: z.number(),
    avgReplySeconds: z.number(),
    loginStatus: loginStatusSchema,
    loginTime: z.string().nullable(),
    checkedAt: z.string().datetime(),
    issues: z.array(issueSchema),
});
export const patrolSummarySchema = z.object({
    storeCount: z.number().int(),
    satisfactionAlerts: z.number().int(),
    replyAlerts: z.number().int(),
    loginAlerts: z.number().int(),
    openAlertStores: z.number().int(),
    overall: z.object({
        satisfaction: z.number(),
        avgReplySeconds: z.number(),
        loginRate: z.number(),
        passingStores: z.number().int(),
    }),
    lastCheckedAt: z.string().datetime().nullable(),
});
export const customerServiceAgentSchema = z.object({
    id: z.string(),
    name: z.string(),
    supervisorId: z.string(),
});
export const supervisorSummarySchema = z.object({
    id: z.string(),
    name: z.string(),
    targetSatisfaction: z.number(),
    agents: z.array(customerServiceAgentSchema.omit({ supervisorId: true })),
    metrics: z.object({
        agentCount: z.number().int(),
        satisfaction: z.number(),
        avgReplySeconds: z.number(),
        loginRate: z.number(),
        onlineAgentCount: z.number().int(),
        passed: z.boolean(),
    }),
});
export const createSupervisorSchema = z.object({
    name: z.string().trim().min(1).max(80),
    targetSatisfaction: z.number().min(0).max(100).default(95),
});
export const updateSupervisorSchema = createSupervisorSchema
    .partial()
    .refine((value) => Object.keys(value).length > 0, "至少需要修改一个字段");
export const assignAgentSchema = z.object({ supervisorId: z.string().min(1) });
export const dashboardResponseSchema = z.object({
    summary: patrolSummarySchema,
    stores: z.array(storeHealthSchema),
    supervisors: z.array(supervisorSummarySchema),
    settings: patrolSettingsSchema,
});
const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const tmallDataSourceSchema = z.enum([
    "DEMO",
    "CSV",
    "MANUAL",
    "CONNECTOR",
]);
export const performanceStoreSchema = z.object({
    id: z.string(),
    name: z.string().trim().min(1),
    enabled: z.boolean(),
});
export const tmallPerformanceRecordSchema = z.object({
    id: z.string(),
    batchId: z.string(),
    dataSource: tmallDataSourceSchema,
    collectedAt: z.string().datetime(),
    importFileName: z.string().nullable(),
    periodStart: dateSchema,
    periodEnd: dateSchema,
    storeName: z.string().trim().min(1),
    agentName: z.string().trim().min(1),
    receptionCount: z.number().int().nonnegative(),
    inquiryCount: z.number().int().nonnegative(),
    orderCount: z.number().int().nonnegative(),
    netSalesAmount: z.number(),
    conversionRate: z.number().min(0).max(100),
    conversionBaseCount: z.number().int().nonnegative(),
    conversionDelayed: z.boolean(),
    avgReplySeconds: z.number().nonnegative(),
    satisfactionRate: z.number().min(0).max(100),
    reviewCount: z.number().int().nonnegative(),
    fiveStarCount: z.number().int().nonnegative(),
    fourStarCount: z.number().int().nonnegative(),
});
export const tmallCollectionBatchSchema = z.object({
    id: z.string(),
    dataSource: tmallDataSourceSchema,
    collectedAt: z.string().datetime(),
    rowCount: z.number().int().nonnegative(),
    importFileName: z.string().nullable(),
    periodStart: dateSchema,
    periodEnd: dateSchema,
});
export const tmallDashboardResponseSchema = z.object({
    records: z.array(tmallPerformanceRecordSchema),
    batches: z.array(tmallCollectionBatchSchema),
    stores: z.array(performanceStoreSchema),
    lastSyncedAt: z.string().datetime(),
    source: z.enum(["DEMO", "IMPORT", "CONNECTOR"]),
    sync: z.object({
        enabled: z.boolean(),
        intervalMinutes: z.number().int().positive(),
        status: z.enum(["READY", "NEEDS_CONFIGURATION"]),
        message: z.string(),
    }),
});
export const importTmallPerformanceSchema = z
    .object({
    storeName: z.string().trim().min(1).max(120),
    periodStart: dateSchema,
    periodEnd: dateSchema,
    fileName: z.string().trim().min(1).max(255),
    csv: z.string().min(1).max(5_000_000),
})
    .refine((value) => value.periodStart <= value.periodEnd, {
    message: "开始日期不能晚于结束日期",
    path: ["periodEnd"],
});
export const tmallExperienceSourceSchema = z.enum([
    "DEMO",
    "LIVE",
    "CONNECTOR",
]);
export const tmallExperienceScalePointSchema = z.object({
    score: z.number().min(0).max(5),
    benchmark: z.string().trim().min(1),
});
export const tmallExperienceMetricSchema = z.object({
    key: z.string().trim().min(1),
    name: z.string().trim().min(1),
    value: z.string().trim().min(1),
    score: z.number().min(0).max(5),
    previousScore: z.number().min(0).max(5).nullable().default(null),
    change: z.string().trim().nullable().default(null),
    changeReason: z.string().trim().nullable().default(null),
    weight: z.number().min(0).max(100),
    target: z.string().trim().nullable(),
    scale: z.array(tmallExperienceScalePointSchema).min(1),
    warning: z.boolean().default(false),
    note: z.string().trim().nullable().default(null),
});
export const tmallExperienceSectionSchema = z.object({
    key: z.string().trim().min(1),
    name: z.string().trim().min(1),
    weight: z.number().min(0).max(100),
    score: z.number().min(0).max(5).nullable().default(null),
    previousScore: z.number().min(0).max(5).nullable().default(null),
    change: z.string().trim().nullable().default(null),
    changeReason: z.string().trim().nullable().default(null),
    metrics: z.array(tmallExperienceMetricSchema),
});
export const tmallExperienceWindowSchema = z.object({
    id: z.string().trim().min(1),
    storeName: z.string().trim().min(1),
    accountName: z.string().trim().min(1),
    source: tmallExperienceSourceSchema,
    pageUrl: z.string().url(),
    collectedAt: z.string().datetime(),
    updatedDate: dateSchema,
    status: z.enum(["CONNECTED", "STALE", "ERROR"]),
    sections: z.array(tmallExperienceSectionSchema),
    overallScore: z.number().min(0).max(5).nullable().default(null),
    previousScore: z.number().min(0).max(5).nullable().default(null),
    overallChange: z.string().trim().nullable().default(null),
    changeReason: z.string().trim().nullable().default(null),
    bonusScore: z.number().nullable().default(null),
});
export const tmallExperienceDashboardResponseSchema = z.object({
    windows: z.array(tmallExperienceWindowSchema),
    storeDirectory: z.array(performanceStoreSchema),
    lastSyncedAt: z.string().datetime(),
    source: z.enum(["DEMO", "LIVE", "CONNECTOR"]),
    sync: z.object({
        enabled: z.boolean(),
        intervalMinutes: z.number().int().positive(),
        status: z.enum(["READY", "WAITING_FOR_WINDOWS", "NEEDS_CONFIGURATION"]),
        message: z.string(),
    }),
});
export const importTmallExperienceSchema = z.object({
    windows: z.array(tmallExperienceWindowSchema).min(1),
});
export const douyinExperienceSourceSchema = z.enum(["DEMO", "LIVE", "CONNECTOR"]);
export const douyinExperienceMetricSchema = z.object({
    key: z.string().trim().min(1),
    name: z.string().trim().min(1),
    score: z.number().min(0).max(100),
    value: z.string().trim().min(1),
    weight: z.number().min(0).max(100),
    weightedScore: z.number().min(0).max(100),
    change: z.string().trim().nullable().default(null),
    previousScore: z.number().min(0).max(100).nullable().default(null),
    changeReason: z.string().trim().nullable().default(null),
    standard: z.string().trim().nullable().default(null),
    warning: z.boolean().default(false),
});
export const douyinExperienceCategorySchema = z.object({
    key: z.string().trim().min(1),
    name: z.string().trim().min(1),
    score: z.number().min(0).max(100),
    weight: z.number().min(0).max(100),
    weightedScore: z.number().min(0).max(100),
    change: z.string().trim().nullable().default(null),
    previousScore: z.number().min(0).max(100).nullable().default(null),
    changeReason: z.string().trim().nullable().default(null),
    metrics: z.array(douyinExperienceMetricSchema),
});
export const douyinExperienceWindowSchema = z.object({
    id: z.string().trim().min(1),
    storeName: z.string().trim().min(1),
    accountName: z.string().trim().min(1),
    source: douyinExperienceSourceSchema,
    pageUrl: z.string().url(),
    collectedAt: z.string().datetime(),
    updatedAt: z.string().trim().min(1),
    status: z.enum(["CONNECTED", "STALE", "ERROR"]),
    overallScore: z.number().min(0).max(100),
    previousScore: z.number().min(0).max(100).nullable().default(null),
    consumerRating: z.number().min(0).max(5).nullable().default(null),
    overallChange: z.string().trim().nullable().default(null),
    changeReason: z.string().trim().nullable().default(null),
    industry: z.string().trim().nullable().default(null),
    validOrderCount: z.number().int().nonnegative().nullable().default(null),
    misconductDeduction: z.number().min(0).default(0),
    categories: z.array(douyinExperienceCategorySchema),
});
export const douyinExperienceStoreSchema = z.object({
    id: z.string().trim().min(1),
    storeName: z.string().trim().min(1),
    status: z.enum(["CONNECTED", "PENDING"]).default("PENDING"),
});
export const douyinExperienceDashboardResponseSchema = z.object({
    windows: z.array(douyinExperienceWindowSchema),
    storeDirectory: z.array(douyinExperienceStoreSchema),
    lastSyncedAt: z.string().datetime(),
    source: z.enum(["DEMO", "LIVE", "CONNECTOR"]),
    sync: z.object({
        enabled: z.boolean(),
        intervalMinutes: z.number().int().positive(),
        status: z.enum(["READY", "WAITING_FOR_WINDOWS", "NEEDS_CONFIGURATION"]),
        message: z.string(),
    }),
});
export const importDouyinExperienceSchema = z.object({
    windows: z.array(douyinExperienceWindowSchema).min(1),
});
// 快手店铺体验分与抖音一样由“总分 + 加权分类 + 分类指标”组成，
// 共用结构但保留平台专属名称，避免前后端把接口和页面混在一起。
export const kuaishouExperienceMetricSchema = douyinExperienceMetricSchema;
export const kuaishouExperienceCategorySchema = douyinExperienceCategorySchema;
export const kuaishouExperienceWindowSchema = douyinExperienceWindowSchema;
export const kuaishouExperienceStoreSchema = douyinExperienceStoreSchema;
export const kuaishouExperienceDashboardResponseSchema = douyinExperienceDashboardResponseSchema;
// 拼多多客服绩效与其他电商平台共用同一套整理字段，但保留独立命名，
// 避免前后端把不同平台的数据源和接口混在一起。
export const pddDataSourceSchema = tmallDataSourceSchema;
export const pddPerformanceRecordSchema = tmallPerformanceRecordSchema;
export const pddCollectionBatchSchema = tmallCollectionBatchSchema;
export const pddDashboardResponseSchema = tmallDashboardResponseSchema;
export const importPddPerformanceSchema = importTmallPerformanceSchema;
export const kuaishouDataSourceSchema = tmallDataSourceSchema;
export const kuaishouPerformanceRecordSchema = tmallPerformanceRecordSchema;
export const kuaishouCollectionBatchSchema = tmallCollectionBatchSchema;
export const kuaishouDashboardResponseSchema = tmallDashboardResponseSchema;
export const importKuaishouPerformanceSchema = importTmallPerformanceSchema;
export const douyinDataSourceSchema = z.enum([
    "DEMO",
    "CSV",
    "MANUAL",
    "CONNECTOR",
]);
export const douyinStoreSchema = performanceStoreSchema;
export const douyinPerformanceRecordSchema = z.object({
    id: z.string(),
    batchId: z.string(),
    dataSource: douyinDataSourceSchema,
    collectedAt: z.string().datetime(),
    importFileName: z.string().nullable(),
    periodStart: dateSchema,
    periodEnd: dateSchema,
    storeName: z.string().trim().min(1),
    agentName: z.string().trim().min(1),
    subAccountName: z.string().trim().default(""),
    receptionCount: z.number().int().nonnegative(),
    dissatisfactionRate: z.number().min(0).max(100),
    satisfactionRate: z.number().min(0).max(100),
    avgReplySeconds: z.number().nonnegative(),
    inquiryConversionRate: z.number().min(0).max(100),
    netSalesAmount: z.number(),
});
export const douyinCollectionBatchSchema = z.object({
    id: z.string(),
    dataSource: douyinDataSourceSchema,
    collectedAt: z.string().datetime(),
    rowCount: z.number().int().nonnegative(),
    importFileName: z.string().nullable(),
    periodStart: dateSchema,
    periodEnd: dateSchema,
    storeName: z.string().trim().min(1),
});
export const douyinCaptureStatusSchema = z.object({
    status: z.enum(["IDLE", "RUNNING", "WAITING_LOGIN", "SUCCEEDED", "FAILED"]),
    startedAt: z.string().datetime().nullable(),
    finishedAt: z.string().datetime().nullable(),
    message: z.string(),
    completedStores: z.number().int().nonnegative(),
    totalStores: z.number().int().nonnegative(),
});
export const douyinDashboardResponseSchema = z.object({
    records: z.array(douyinPerformanceRecordSchema),
    batches: z.array(douyinCollectionBatchSchema),
    stores: z.array(douyinStoreSchema),
    lastSyncedAt: z.string().datetime(),
    source: z.enum(["DEMO", "IMPORT", "CONNECTOR"]),
    sync: z.object({
        enabled: z.boolean(),
        intervalMinutes: z.number().int().positive(),
        status: z.enum(["READY", "NEEDS_CONFIGURATION"]),
        message: z.string(),
    }),
    capture: douyinCaptureStatusSchema,
});
export const importDouyinPerformanceSchema = z
    .object({
    storeName: z.string().trim().min(1).max(120),
    periodStart: dateSchema,
    periodEnd: dateSchema,
    fileName: z.string().trim().min(1).max(255),
    csv: z.string().min(1).max(5_000_000),
    dataSource: z.enum(["CSV", "CONNECTOR"]).optional(),
})
    .refine((value) => value.periodStart <= value.periodEnd, {
    message: "开始日期不能晚于结束日期",
    path: ["periodEnd"],
});
